import { z } from 'zod';

/** Why a contact is re-synced; only `order_paid` sets the marketing consent (Brevo blocklist status). */
export const contactSyncReason = z.enum([
  'order_paid',
  'refund',
  /** The participant declared a cancellation to the insurer (decision 0025). */
  'insurance_claim',
  'documents',
  'profile',
  'reconcile',
]);
export type ContactSyncReason = z.infer<typeof contactSyncReason>;

/** Messages pushed on the Cloudflare Queue, consumed by apps/api. */
export const queueMessage = z.discriminatedUnion('type', [
  z.object({
    type: z.literal('brevo.contact.upsert'),
    email: z.string().email(),
    listIds: z.array(z.number().int()),
    unlinkListIds: z.array(z.number().int()).default([]),
    attributes: z.record(z.union([z.string(), z.number(), z.boolean()])).default({}),
    /** Brevo blocklist status: `subscribe`, or blocklist the contact only if it is new (decision 0013). */
    marketing: z.enum(['subscribe', 'opt_out_if_new']).optional(),
  }),
  /** Mirror one participant's registration truth to Brevo (lists + attributes), read from the DB at run time. */
  z.object({
    type: z.literal('brevo.contact.sync'),
    eventId: z.string().uuid(),
    userId: z.string().uuid(),
    reason: contactSyncReason,
  }),
  z.object({
    type: z.literal('brevo.email.send'),
    templateId: z.number().int(),
    to: z.string().email(),
    params: z.record(z.unknown()).default({}),
    idempotencyKey: z.string(),
  }),
  z.object({
    type: z.literal('order.paid'),
    orderId: z.string().uuid(),
  }),
  /** The confirmation email again, with fresh links ("Resend the email", record 0026 D5). */
  z.object({
    type: z.literal('order.confirmation.resend'),
    orderId: z.string().uuid(),
    idempotencyKey: z.string(),
  }),
  /** Declare a paid insured order to the insurer (Beticketing `register`, decision 0025). */
  z.object({
    type: z.literal('insurance.declare'),
    eventId: z.string().uuid(),
    orderId: z.string().uuid(),
  }),
  /** A refunded insured order: withdraw its insurance (Beticketing `retract`). */
  z.object({
    type: z.literal('insurance.retract'),
    eventId: z.string().uuid(),
    orderId: z.string().uuid(),
  }),
]);
export type QueueMessage = z.infer<typeof queueMessage>;
